import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_covenant-coaching/artifacts/qvrfkj4f_hands.jfif";

const lines = ["Marriages heal", "one conversation", "at a time."];

const LineReveal = ({ text, index, italicWord }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.35 + index * 0.16, ease: [0.22, 1, 0.36, 1] }}
    >
      {italicWord ? (
        <>
          one <em className="italic text-[#8A5A44]">conversation</em>
        </>
      ) : (
        text
      )}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section ref={ref} className="relative min-h-screen pt-32 pb-20 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 gap-8 items-center">
        <motion.div style={{ y: textY }} className="col-span-12 lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-xs uppercase tracking-[0.25em] text-[#8A5A44] mb-8"
            data-testid="hero-eyebrow"
          >
            Christian Marriage Coaching · Greensboro, NC · Video calls nationwide
          </motion.p>

          <h1
            className="font-serif-display font-medium text-[#2C2A28] tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-[5.25rem]"
            data-testid="hero-heading"
          >
            {lines.map((l, i) => (
              <LineReveal key={l} text={l} index={i} italicWord={i === 1} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-8 max-w-xl text-base md:text-lg text-[#5C5854] leading-relaxed font-light"
            data-testid="hero-subtext"
          >
            We're a faith rooted ministry led by a couple married 41 years- So in love they've
            been married 3 times! We coach couples toward the fullness God intended, free of
            charge. No price tag on a gift from God.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("book")}
              className="group rounded-full bg-[#8A5A44] hover:bg-[#734A38] text-[#FAF9F6] px-8 py-4 text-sm flex items-center gap-2 transition-colors duration-300"
              data-testid="hero-cta-conversation"
            >
              Start a conversation
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToSection("story")}
              className="rounded-full border border-[#C4B299] hover:border-[#8A5A44] text-[#2C2A28] px-8 py-4 text-sm transition-colors duration-300"
              data-testid="hero-cta-story"
            >
              Meet the Wicks
            </button>
          </motion.div>
        </motion.div>

        <div className="col-span-12 lg:col-span-5 relative lg:-ml-8 mt-12 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
          >
            <motion.img
              style={{ y: imgY }}
              src={HERO_IMG}
              alt="A couple holding hands, decades of marriage"
              className="w-full h-[420px] lg:h-[560px] object-cover scale-110"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C2A28]/30 to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.5 }}
            className="absolute -bottom-8 -left-4 lg:-left-12 bg-[#F3EFEA] rounded-2xl px-8 py-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-black/5"
            data-testid="hero-stat-card"
          >
            <p className="font-serif-display text-4xl font-semibold text-[#8A5A44]">41 years</p>
            <p className="text-xs text-[#5C5854] mt-1 tracking-wide">married. Through it all.</p>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        onClick={() => scrollToSection("struggles")}
        className="absolute bottom-8 left-6 lg:left-10 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C5854] hover:text-[#8A5A44] transition-colors duration-300"
        data-testid="hero-scroll-hint"
      >
        <ArrowDown size={14} className="animate-bounce" /> Scroll
      </motion.button>
    </section>
  );
};
